import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, User, LayoutDashboard, Users, Settings, LogOut, Briefcase, Trophy, UserPen } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { getCurrentUser, logout } from '../utils/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../services/api';
import CoachIcon from './icons/CoachIcon';

const ProfileDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userStats, setUserStats] = useState({ postsCount: 0, followers: 0, following: 0 });

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Fetch real-time user stats when drawer opens
  useEffect(() => {
    const fetchUserStats = async () => {
      if (isOpen && currentUser?.username) {
        try {
          const response = await userAPI.getUserProfile(currentUser.username);
          setUserStats({
            postsCount: response.data.postsCount || 0,
            followers: response.data.followers || 0,
            following: response.data.following || 0
          });
        } catch (error) {
          console.error('Failed to fetch user stats:', error);
        }
      }
    };
    fetchUserStats();
  }, [isOpen, currentUser?.username]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: User,
      label: 'My Profile',
      path: `/profile/${currentUser?.username || ''}`,
      description: 'View and edit your profile'
    },
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/dashboard',
      description: 'Your stats and analytics'
    },
    {
      icon: Users,
      label: 'Connections',
      path: '/connections',
      description: 'Manage your network'
    },
    {
      icon: Trophy,
      label: 'Trials',
      path: '/trials',
      description: 'Browse sports trials'
    },
    {
      icon: Briefcase,
      label: 'Sports Jobs',
      path: '/jobs',
      description: 'Find job opportunities'
    },
    {
      icon: CoachIcon,
      label: 'iCoach',
      path: '/ask-ai',
      description: 'AI-powered sports assistant'
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/settings',
      description: 'Account preferences'
    },
  ];

  if (!isOpen) return null;
  if (!currentUser) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed left-0 top-0 bottom-0 w-80 bg-card z-50 shadow-2xl transform transition-transform duration-300 flex flex-col">
        {/* Header with Cover Image - Fixed at top */}
        <div 
          className="relative h-32 bg-cover bg-center flex-shrink-0" 
          style={{
            backgroundImage: currentUser?.coverImage 
              ? `linear-gradient(rgba(139, 92, 246, 0.3), rgba(139, 92, 246, 0.3)), url(${currentUser.coverImage})`
              : undefined,
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Profile Info - Outside scrollable area to maintain negative margin overlap */}
        <div className="px-5 -mt-12 mb-6 relative z-10 flex-shrink-0">
          <Avatar className="w-20 h-20 border-4 border-card shadow-lg ring-2 ring-purple-100">
            <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
            <AvatarFallback className="text-2xl bg-purple-100 text-purple-700 font-semibold">{currentUser.name[0]}</AvatarFallback>
          </Avatar>
          <h2 className="mt-3 text-lg font-bold text-foreground leading-tight">{currentUser.name}</h2>
          <p className="text-sm text-muted-foreground">@{currentUser.username}</p>
          
          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                onClose();
                navigate(`/profile/${currentUser.username}`);
              }}
              title="View your posts"
            >
              <p className="text-base font-bold text-primary">
                {userStats.postsCount.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Posts</p>
            </div>
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                onClose();
                navigate('/connections?tab=followers');
              }}
              title="View your followers"
            >
              <p className="text-base font-bold text-primary">
                {userStats.followers.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Followers</p>
            </div>
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                onClose();
                navigate('/connections?tab=following');
              }}
              title="View who you're following"
            >
              <p className="text-base font-bold text-primary">
                {userStats.following.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Following</p>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Menu Items */}
          <div className="px-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-all group"
                >
                  <div className="w-9 h-9 rounded-xl bg-muted/50 flex items-center justify-center group-hover:bg-purple-100 transition-all">
                    <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-foreground leading-tight">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Edit Profile Button */}
          <div className="px-4 mt-4 mb-6 pb-4">
            <Link to="/edit-profile" onClick={onClose}>
              <Button
                variant="outline"
                className="w-full justify-start space-x-3 h-auto py-2.5 px-3 rounded-xl border-primary/20 hover:bg-primary/5 hover:border-primary/30 transition-all"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <UserPen className="w-4 h-4 text-primary" />
                </div>
                <span className="text-primary font-semibold text-sm">Edit Profile</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileDrawer;
